// Shared client for the task-tracking API (/api/task-tracking on the main
// server). Injects the JWT from localStorage on every call. Mirrors the
// inboxApi helper pattern.
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const BASE = `${API_URL}/api/task-tracking`;

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Build an absolute URL for a relative task asset path (e.g. chat image).
export const taskAssetUrl = (relative) => (relative ? `${API_URL}${relative}` : '');

export const tasksApi = {
  bootstrap: () => axios.get(`${BASE}/bootstrap`, { headers: authHeader() }).then((r) => r.data),
  users: () => axios.get(`${BASE}/users`, { headers: authHeader() }).then((r) => r.data),

  // Configurable lead quick actions (for labelling lead activity in analytics).
  leadActions: () => axios.get(`${API_URL}/api/client-markers/lead-actions`, { headers: authHeader() }).then((r) => r.data),

  // Per-user dashboard: { updates: [{department, tasks[]}], mine: [{department, tasks[]}] }.
  dashboard: () => axios.get(`${BASE}/dashboard`, { headers: authHeader() }).then((r) => r.data),

  // Departments
  createDepartment: (body) => axios.post(`${BASE}/departments`, body, { headers: authHeader() }).then((r) => r.data),
  updateDepartment: (id, body) => axios.patch(`${BASE}/departments/${id}`, body, { headers: authHeader() }).then((r) => r.data),
  deleteDepartment: (id) => axios.delete(`${BASE}/departments/${id}`, { headers: authHeader() }).then((r) => r.data),

  // Task types
  listTaskTypes: () => axios.get(`${BASE}/task-types`, { headers: authHeader() }).then((r) => r.data),
  createTaskType: (body) => axios.post(`${BASE}/task-types`, body, { headers: authHeader() }).then((r) => r.data),
  updateTaskType: (id, body) => axios.patch(`${BASE}/task-types/${id}`, body, { headers: authHeader() }).then((r) => r.data),
  deleteTaskType: (id) => axios.delete(`${BASE}/task-types/${id}`, { headers: authHeader() }).then((r) => r.data),

  // Detail field library (reusable field definitions)
  listFieldDefs: () => axios.get(`${BASE}/field-defs`, { headers: authHeader() }).then((r) => r.data),
  createFieldDef: (body) => axios.post(`${BASE}/field-defs`, body, { headers: authHeader() }).then((r) => r.data),
  updateFieldDef: (id, body) => axios.patch(`${BASE}/field-defs/${id}`, body, { headers: authHeader() }).then((r) => r.data),
  deleteFieldDef: (id) => axios.delete(`${BASE}/field-defs/${id}`, { headers: authHeader() }).then((r) => r.data),

  // Tasks
  listTasks: (departmentSlug) =>
    axios.get(`${BASE}/tasks`, { headers: authHeader(), params: departmentSlug ? { department: departmentSlug } : {} }).then((r) => r.data),
  getTask: (id) => axios.get(`${BASE}/tasks/${id}`, { headers: authHeader() }).then((r) => r.data),
  createTask: (body) => axios.post(`${BASE}/tasks`, body, { headers: authHeader() }).then((r) => r.data),
  updateTask: (id, body) => axios.patch(`${BASE}/tasks/${id}`, body, { headers: authHeader() }).then((r) => r.data),
  deleteTask: (id) => axios.delete(`${BASE}/tasks/${id}`, { headers: authHeader() }).then((r) => r.data),
  postMessage: (id, body) => axios.post(`${BASE}/tasks/${id}/messages`, body, { headers: authHeader() }).then((r) => r.data),
  markRead: (id) => axios.post(`${BASE}/tasks/${id}/read`, {}, { headers: authHeader() }).then((r) => r.data),

  // Upload a chat image; returns { url, w, h }.
  uploadAttachment: (id, file) => {
    const form = new FormData();
    form.append('image', file);
    return axios.post(`${BASE}/tasks/${id}/attachments`, form, {
      headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }
    }).then((r) => r.data);
  },

  // Engagement actions (task_open, message_read, upload). Fire-and-forget.
  recordActivity: (id, type) => axios.post(`${BASE}/tasks/${id}/activity`, { type }, { headers: authHeader() }).then((r) => r.data),

  // Analytics heatmap. tz = minutes east of UTC so day boundaries match the viewer.
  analytics: (period) =>
    axios.get(`${BASE}/analytics`, {
      headers: authHeader(),
      params: { period, tz: -new Date().getTimezoneOffset() }
    }).then((r) => r.data),

  // Per-department breakdown: created/completed counts per user per task type.
  analyticsByDepartment: (departmentId) =>
    axios.get(`${BASE}/analytics/by-department`, {
      headers: authHeader(),
      params: { department: departmentId }
    }).then((r) => r.data),

  // Per-person report over a from/to date range (YYYY-MM-DD, `to` inclusive).
  userReport: (userId, from, to) =>
    axios.get(`${BASE}/analytics/user/${userId}`, {
      headers: authHeader(),
      params: { from, to, tz: -new Date().getTimezoneOffset() }
    }).then((r) => r.data)
};

export default tasksApi;
